const stripOrgPk = (s?: string | null) => (typeof s === 'string' && s.startsWith('ORG_') ? s.slice(4) : s || null)

const looksLikeUuid = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)

// More permissive UUID-like pattern (any 8-4-4-4-12 hex format)
const looksLikeUuidPermissive = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)

const resolveOrgIdByName = (name: string, orgs: any[]) => {
  const n = (name || '').trim().toLowerCase()
  const hit = orgs?.find(o => (o?.name || '').trim().toLowerCase() === n)
  return hit?.org_uuid ?? null
}

// Check if a string matches an org_uuid in the orgs array
const resolveOrgIdByUuid = (uuid: string, orgs: any[]) => {
  const hit = orgs?.find(o => o?.org_uuid === uuid)
  return hit?.org_uuid ?? null
}

const toOrgId = (item: any, orgs: any[]): string | null => {
  if (!item) return null

  // strings
  if (typeof item === 'string') {
    // could be PK, UUID, or a name label
    const pkOrUuid = stripOrgPk(item.trim())
    if (pkOrUuid) {
      // First try strict UUID format
      if (looksLikeUuid(pkOrUuid)) return pkOrUuid
      // Then try permissive UUID format
      if (looksLikeUuidPermissive(pkOrUuid)) return pkOrUuid
      // Then check if it exists in the orgs array directly
      const foundByUuid = resolveOrgIdByUuid(pkOrUuid, orgs)
      if (foundByUuid) return foundByUuid
    }
    // Fall back to name lookup
    return resolveOrgIdByName(item, orgs)
  }

  // objects
  if (typeof item === 'object') {
    // common shapes
    if (item.org_uuid) return item.org_uuid
    if (item.PK) {
      const id = stripOrgPk(item.PK)
      if (id && (looksLikeUuid(id) || looksLikeUuidPermissive(id))) return id
      // Check if it exists in the orgs array
      if (id) {
        const foundByUuid = resolveOrgIdByUuid(id, orgs)
        if (foundByUuid) return foundByUuid
      }
    }
    if (item.id) {
      const id = stripOrgPk(String(item.id))
      if (id && (looksLikeUuid(id) || looksLikeUuidPermissive(id))) return id
      // Check if it exists in the orgs array
      if (id) {
        const foundByUuid = resolveOrgIdByUuid(id, orgs)
        if (foundByUuid) return foundByUuid
      }
    }
    // nested value/org
    if (item.value) {
      if (typeof item.value === 'string') {
        const id = stripOrgPk(item.value)
        if (id && (looksLikeUuid(id) || looksLikeUuidPermissive(id))) return id
        // Check if it exists in the orgs array
        if (id) {
          const foundByUuid = resolveOrgIdByUuid(id, orgs)
          if (foundByUuid) return foundByUuid
        }
      }
      if (typeof item.value === 'object' && item.value.org_uuid) return item.value.org_uuid
    }
    if (item.org && item.org.org_uuid) return item.org.org_uuid
    // final fallback: name label on the object
    if (item.name) return resolveOrgIdByName(item.name, orgs)
  }

  return null
}

export const normalizeContractedBy = (arr: any, orgs: any[]): string[] => {
  if (!Array.isArray(arr)) return []
  const ids = arr.map(it => toOrgId(it, orgs)).filter((v): v is string => !!v)
  return Array.from(new Set(ids))
}
